import type { DatePickerProps, TimePickerProps } from 'antd'
import { Button, Col, DatePicker, Form, Modal, Select, Space, TimePicker } from 'antd'
import { RangePickerProps } from 'antd/lib/date-picker'
import dayjs from 'dayjs'
import { Moment } from 'moment'
import React, { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { dateTime } from '@/configs'
import { VOLUME_OPTIONS } from '@/constants/machine'
import { useCustomers } from '@/pages/Customer/Customers.hooks'
import { useEmployees } from '@/pages/Employees/Employees.hooks'
import { defaultFilterDate, FilterDate } from '@/pages/WashingHistory'
import { useWashingHistoryByDay } from '@/pages/WashingHistory/WashingHistoryByDay.hooks'
import { useWashingMethods } from '@/pages/WashMethods/WashingMethod.hooks'
import { DateType, Machine } from '@/types'
import { ExportWashingHistoryByDayRequest } from '@/types/apis/washingHistory/ExportWashingHistoryByDayRequest'
import { ExportWashingHistoryByRangeDayRequest } from '@/types/apis/washingHistory/ExportWashingHistoryByRangeDayRequest'
import { GetWashingHistoryByDayRequest } from '@/types/apis/washingHistory/GetWashingHistoryByDayRequest'
import { GetWashingHistoryByRangeDayRequest } from '@/types/apis/washingHistory/GetWashingHistoryByRangeDayRequest'
import { LaundryRequest } from '@/types/apis/washingHistory/LaundryRequest'
import { formatDateSendRequest } from '@/utils/date'

import { SpaceCustom } from '../../style'
import {
  ButtonCustom,
  ButtonCustomModal,
  ButtonWrapper,
  CustomSelect,
  CustomSelectModal,
  DatePickerCustom,
  Icon,
  RangeDateWrapper,
  SearchDayWrapper,
  SelectWrapper,
  SelectWrapperCustom,
  TitleSearch,
  WrapperFilter,
} from '../styles'

type Props = {
  setDateFilter: React.Dispatch<React.SetStateAction<Moment | null | undefined>>
  getWashingHistoryByDay: (request: GetWashingHistoryByDayRequest) => Promise<void>
  getWashingHistoryByRangeDay: (request: GetWashingHistoryByRangeDayRequest) => Promise<void>
  dateFilter?: Moment | null
  washingMachines: Machine[]
  exportExcelWashingHistoryByDay: (request: ExportWashingHistoryByDayRequest) => Promise<void>
  exportExcelWashingHistoryByRangeDay: (request: ExportWashingHistoryByRangeDayRequest) => Promise<void>
  exportWashingHistoryByDayLoading: boolean
  setDateRangeFilter: React.Dispatch<React.SetStateAction<FilterDate>>
  dateRangeFilter: FilterDate
  isReloadByDate: boolean
  isReloadByRangeDate: boolean
}

const FilterWashingHistoryByDay: FC<Props> = ({
  getWashingHistoryByDay,
  setDateFilter,
  dateFilter,
  washingMachines,
  exportExcelWashingHistoryByDay,
  exportExcelWashingHistoryByRangeDay,
  exportWashingHistoryByDayLoading,
  setDateRangeFilter,
  dateRangeFilter,
  getWashingHistoryByRangeDay,
  isReloadByDate,
  isReloadByRangeDate,
}) => {
  const { t } = useTranslation()
  const format = 'HH:mm'

  const [form] = Form.useForm<LaundryRequest>()
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | undefined>()
  const [selectedCustomerIdByRangeDay, setSelectedCustomerIdByRangeDay] = useState<string | undefined>()
  const [selectedMachineId, setSelectedMachineId] = useState<string | undefined>()
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [date, setDate] = useState<string>()
  const [time, setTime] = useState<string | undefined>()

  const handlePickByDay = (date: Moment | null) => {
    setDateRangeFilter((pre) => ({ ...pre, type: DateType.BY_DAY }))
    setDateFilter(date)
    getWashingHistoryByDay({
      date: formatDateSendRequest(date),
      customerId: selectedCustomerId,
      washingMachineId: selectedMachineId,
    })
  }

  const handlePickRangeDay = (type: string, date: Moment | null) => {
    switch (type) {
      case 'fromByRangeDate':
        setDateRangeFilter((pre) => ({ ...pre, type: DateType.RANGE_DAY, fromDate: date }))
        date &&
          dateRangeFilter.toDate &&
          getWashingHistoryByRangeDay({
            fromDate: formatDateSendRequest(date),
            toDate: formatDateSendRequest(dateRangeFilter.toDate),
            customerId: selectedCustomerIdByRangeDay,
          })
        break

      case 'toByRangeDate':
        setDateRangeFilter((pre) => ({ ...pre, type: DateType.RANGE_DAY, toDate: date }))
        getWashingHistoryByRangeDay({
          fromDate: formatDateSendRequest(dateRangeFilter.fromDate),
          toDate: formatDateSendRequest(date),
          customerId: selectedCustomerIdByRangeDay,
        })
        break

      default:
        break
    }
  }

  const handleChangeCustomer = (value: string | undefined) => {
    setSelectedCustomerId(value)
    setSelectedCustomerIdByRangeDay(value)
    if (dateFilter) {
      getWashingHistoryByDay({
        date: formatDateSendRequest(dateFilter ?? null),
        customerId: value,
        washingMachineId: selectedMachineId,
      })
    }
    if (dateRangeFilter.fromDate && dateRangeFilter.toDate) {
      getWashingHistoryByRangeDay({
        fromDate: formatDateSendRequest(dateRangeFilter.fromDate),
        toDate: formatDateSendRequest(dateRangeFilter.toDate),
        customerId: value,
      })
    }
  }

  const handleChangeMachine = (value: string | undefined) => {
    setSelectedMachineId(value)
    getWashingHistoryByDay({
      date: formatDateSendRequest(dateFilter ?? null),
      customerId: selectedCustomerId,
      washingMachineId: value,
    })
  }

  const handleExportCSV = () => {
    if (dateFilter) {
      exportExcelWashingHistoryByDay({
        date: formatDateSendRequest(dateFilter),
        customerId: selectedCustomerId,
        washingMachineId: selectedMachineId,
      })
    }
  }

  const handleExportCSVByRangeDay = () => {
    if (dateRangeFilter) {
      exportExcelWashingHistoryByRangeDay({
        fromDate: formatDateSendRequest(dateRangeFilter.fromDate),
        toDate: formatDateSendRequest(dateRangeFilter.toDate),
        customerId: selectedCustomerIdByRangeDay,
      })
    }
  }

  const handleDownLoadCSV = (type: DateType) => {
    switch (type) {
      case DateType.BY_DAY:
        handleExportCSV()
        break
      case DateType.RANGE_DAY:
        handleExportCSVByRangeDay()
        break
      default:
        break
    }
  }

  const handleReset = () => {
    setDateFilter(null)
    setDateRangeFilter(defaultFilterDate)
    setSelectedCustomerId(undefined)
    setSelectedCustomerIdByRangeDay(undefined)
    setSelectedMachineId(undefined)
  }

  const { getAllEmployees, allEmployeesResponse } = useEmployees()
  const { getAllCustomer, customers } = useCustomers()
  const { getAllWashingMethods, washingMethods } = useWashingMethods()
  const { laundryLoading, createLaundry } = useWashingHistoryByDay()

  useEffect(() => {
    getAllEmployees({}), getAllCustomer(), getAllWashingMethods()
  }, [getAllCustomer, getAllEmployees, getAllWashingMethods])

  useEffect(() => {
    if (isReloadByDate && dateFilter) {
      getWashingHistoryByDay({
        date: formatDateSendRequest(dateFilter),
        customerId: selectedCustomerId,
        washingMachineId: selectedMachineId,
      })
    }

    if (isReloadByRangeDate && dateRangeFilter.fromDate && dateRangeFilter.toDate) {
      getWashingHistoryByRangeDay({
        fromDate: formatDateSendRequest(dateRangeFilter.fromDate),
        toDate: formatDateSendRequest(dateRangeFilter.toDate),
        customerId: selectedCustomerIdByRangeDay,
      })
    }
  })

  const handleSubmit = async (value: LaundryRequest) => {
    const laundryTime = `${date} ${time}`
    const payload: LaundryRequest = {
      ...value,
      time: laundryTime,
      isAdmin: true,
    }

    await createLaundry(payload)
    setIsOpen(false)
    form.resetFields()
  }

  const handleChangeWashingMethod = (value: number) => {
    const washingMethod = washingMethods.find((item) => item.id === value)
    if (washingMethod) {
      form.setFieldsValue({ volumes: washingMethod.volume })
    }
  }

  const onChangeDatePicker: DatePickerProps['onChange'] = (_, dateStr) => {
    setDate(dateStr)
  }

  const onChangeTimePicker: TimePickerProps['onChange'] = (time) => {
    setTime(time?.format('HH:mm'))
  }

  const disabledDate: RangePickerProps['disabledDate'] = (current) => {
    // Can not select days before today and today
    return current && current >= dayjs().endOf('day')
  }

  return (
    <WrapperFilter gutter={16}>
      <Col span={10}>
        <ButtonWrapper>
          <SearchDayWrapper>
            <TitleSearch>{t('components.pages.washingHistory.listReport.filter.filterDay')}</TitleSearch>
            <RangeDateWrapper>
              <DatePickerCustom
                value={dateFilter}
                format={dateTime.dateFormat}
                onChange={(date) => handlePickByDay(date)}
                disabled={dateRangeFilter.type !== DateType.BY_DAY && dateRangeFilter.type !== DateType.NO_SELECT}
              />
            </RangeDateWrapper>
          </SearchDayWrapper>
        </ButtonWrapper>
        <ButtonWrapper>
          <SearchDayWrapper>
            <TitleSearch>{t('components.pages.washingHistory.listReport.filter.filterByRangeDay')}</TitleSearch>
            <RangeDateWrapper>
              <DatePickerCustom
                value={dateRangeFilter.fromDate}
                onChange={(date) => handlePickRangeDay('fromByRangeDate', date)}
                format={dateTime.dateFormat}
                disabled={dateRangeFilter.type !== DateType.RANGE_DAY && dateRangeFilter.type !== DateType.NO_SELECT}
              />
              <Icon>~</Icon>
              <DatePickerCustom
                value={dateRangeFilter.toDate}
                onChange={(date) => handlePickRangeDay('toByRangeDate', date)}
                format={dateTime.dateFormat}
                disabled={dateRangeFilter.type !== DateType.RANGE_DAY && dateRangeFilter.type !== DateType.NO_SELECT}
              />
            </RangeDateWrapper>
          </SearchDayWrapper>
        </ButtonWrapper>
      </Col>
      <Col span={8}>
        <ButtonWrapper>
          <SearchDayWrapper>
            <TitleSearch>{t('components.pages.washingHistory.listReport.filter.filterMachine')}</TitleSearch>
            <SelectWrapper>
              <CustomSelect
                allowClear
                onChange={handleChangeMachine}
                value={selectedMachineId}
                disabled={!(dateRangeFilter.type !== DateType.RANGE_DAY && dateRangeFilter.type !== DateType.NO_SELECT)}
              >
                {washingMachines.map((machine) => (
                  <Select.Option key={machine.id} value={machine.id}>
                    {machine.name}
                  </Select.Option>
                ))}
              </CustomSelect>
            </SelectWrapper>
          </SearchDayWrapper>
        </ButtonWrapper>
        <ButtonWrapper>
          <SearchDayWrapper>
            <TitleSearch>{t('components.pages.washingHistory.listReport.filter.filterCustomerName')}</TitleSearch>
            <SelectWrapper>
              <CustomSelect
                allowClear
                value={selectedCustomerId}
                onChange={(value: string) => handleChangeCustomer(value)}
              >
                {customers?.map((customer) => (
                  <Select.Option key={customer.id} value={customer.id}>
                    {customer.shortName || ''}
                  </Select.Option>
                ))}
              </CustomSelect>
            </SelectWrapper>
          </SearchDayWrapper>
        </ButtonWrapper>
      </Col>
      <Col span={6}>
        <Space>
          {dateFilter || dateRangeFilter.type ? (
            <ButtonCustom danger onClick={handleReset}>
              {t('components.pages.washingCount.listReport.table.reset')}
            </ButtonCustom>
          ) : null}
          <ButtonCustom
            loading={exportWashingHistoryByDayLoading}
            onClick={() => handleDownLoadCSV(dateRangeFilter.type)}
          >
            {t('components.pages.washingCount.listReport.table.export')}
          </ButtonCustom>
        </Space>
      </Col>
      <Col span={8}>
        <ButtonCustomModal onClick={() => setIsOpen(true)}>+</ButtonCustomModal>
      </Col>

      {/* Modal laundry */}
      <Modal
        title={t('components.pages.washingHistory.listReport.modal.title')}
        visible={isOpen}
        onCancel={() => setIsOpen(false)}
        footer={null}
      >
        <div>
          <Form layout="vertical" onFinish={handleSubmit} form={form}>
            <SelectWrapperCustom>
              <Form.Item
                label={t('components.pages.washingHistory.listReport.modal.operatorName')}
                name="operatorId"
                rules={[
                  {
                    required: true,
                    message: t('components.pages.washingHistory.listReport.modal.requireOperatorName'),
                  },
                ]}
              >
                <CustomSelectModal>
                  {allEmployeesResponse?.map((employee) => (
                    <Select.Option key={employee.id} value={employee.id}>
                      {`${employee.lastName} ${employee.firstName}`}
                    </Select.Option>
                  ))}
                </CustomSelectModal>
              </Form.Item>
            </SelectWrapperCustom>
            <SelectWrapperCustom>
              <Form.Item
                label={t('components.pages.washingHistory.listReport.modal.washingMachine')}
                name="washingMachineId"
                rules={[
                  {
                    required: true,
                    message: t('components.pages.washingHistory.listReport.modal.requireWashingMachine'),
                  },
                ]}
              >
                <CustomSelectModal>
                  {washingMachines?.map((washingMachine) => (
                    <Select.Option key={washingMachine.id} value={washingMachine.id}>
                      {washingMachine.name}
                    </Select.Option>
                  ))}
                </CustomSelectModal>
              </Form.Item>
            </SelectWrapperCustom>
            <SelectWrapperCustom>
              <Form.Item
                label={t('components.pages.washingHistory.listReport.modal.customerName')}
                name="customerId"
                rules={[
                  {
                    required: true,
                    message: t('components.pages.washingHistory.listReport.modal.requireCustomerName'),
                  },
                ]}
              >
                <CustomSelectModal>
                  {customers?.map((customer) => (
                    <Select.Option key={customer.id} value={customer.id}>
                      {customer.shortName}
                    </Select.Option>
                  ))}
                </CustomSelectModal>
              </Form.Item>
            </SelectWrapperCustom>
            <SelectWrapperCustom>
              <Form.Item
                label={t('components.pages.washingHistory.listReport.modal.washingMethod')}
                name="washingMethodId"
                rules={[
                  {
                    required: true,
                    message: t('components.pages.washingHistory.listReport.modal.requireWashingMethod'),
                  },
                ]}
              >
                <CustomSelectModal onChange={handleChangeWashingMethod}>
                  {washingMethods?.map((washingMethod) => (
                    <Select.Option key={washingMethod.id} value={washingMethod.id}>
                      {washingMethod.name}
                    </Select.Option>
                  ))}
                </CustomSelectModal>
              </Form.Item>
            </SelectWrapperCustom>
            <SelectWrapperCustom>
              <Form.Item
                label={t('components.pages.washingHistory.listReport.modal.volume')}
                name="volumes"
                rules={[
                  { required: true, message: t('components.pages.washingHistory.listReport.modal.requireVolume') },
                ]}
              >
                <CustomSelectModal style={{ border: '1px solid black', borderRadius: '4px' }}>
                  {VOLUME_OPTIONS.map((options) => (
                    <Select.Option key={options.value} value={options.value}>
                      {options.label}
                    </Select.Option>
                  ))}
                </CustomSelectModal>
              </Form.Item>
            </SelectWrapperCustom>
            <SelectWrapperCustom>
              <Form.Item
                label={t('components.pages.washingHistory.listReport.modal.time')}
                name="time"
                rules={[
                  {
                    required: !(date && time),
                    message: t('components.pages.washingHistory.listReport.modal.requireTime'),
                  },
                ]}
              >
                <DatePicker disabledDate={disabledDate} onChange={onChangeDatePicker} />
                <TimePicker format={format} showNow={false} onChange={onChangeTimePicker} />
              </Form.Item>
            </SelectWrapperCustom>
            <SpaceCustom>
              <Button type="primary" htmlType="submit" loading={laundryLoading}>
                {t('components.pages.washingHistory.listReport.modal.submit')}
              </Button>
            </SpaceCustom>
          </Form>
        </div>
      </Modal>
    </WrapperFilter>
  )
}

FilterWashingHistoryByDay.displayName = 'FilterWashingHistoryByDay'
export default FilterWashingHistoryByDay
